import axios from 'axios';
import { FABAI_BASE_URL } from '../../config/api';

/**
 * Direct browser → AI service calls. The form URL carries `crmJwtToken` (and
 * optionally `crmTenantId`), which is all the AI service needs — the same
 * auth the form-builder backend used to forward when it proxied these calls.
 */

export interface FabaiPersona {
  id?: string;
  _id?: string;
  name?: string;
  label?: string;
  [key: string]: any;
}

export interface FabaiGenerateOptions {
  /** The runtime CRM tenant: `crmTenantId` from the form URL, else the form's tenant. */
  tenantId: string;
  /** `crmJwtToken` from the form URL. */
  jwtToken?: string;
  payload: Record<string, any>;
}

/**
 * Runs one generation. The response is the raw AI service envelope; the
 * caller extracts the text with `extractAiText` (see ../CustomBlocks/aiGenerator/aiBlocks).
 */
export const generateFabai = async ({ tenantId, jwtToken, payload }: FabaiGenerateOptions): Promise<any> => {
  const response = await axios.post(
    `${FABAI_BASE_URL}/tenant/${tenantId}/fabai/generate`,
    { data: payload },
    {
      headers: { Authorization: `Bearer ${jwtToken || ''}` },
      timeout: 60000,
    },
  );
  return response.data;
};

/**
 * The tenant's personas, for the field's persona setting. Cached in
 * localStorage (as the CRM's own AI generator does) so the list survives
 * reloads and repeated opens of the settings panel.
 */
export const fetchPersonas = async (tenantId: string, jwtToken?: string): Promise<FabaiPersona[]> => {
  const cacheKey = `fabaiPersonas:${tenantId}`;
  try {
    const cached = localStorage.getItem(cacheKey);
    if (cached) return JSON.parse(cached);
  } catch {
    // A corrupt cache should never block the selector — fall through to refetch.
  }

  const response = await axios.get(`${FABAI_BASE_URL}/tenant/${tenantId}/persona/autocomplete`, {
    params: { limit: 100, isActive: true },
    headers: { Authorization: `Bearer ${jwtToken || ''}` },
    timeout: 15000,
  });
  const personas = Array.isArray(response.data) ? response.data : [];
  try {
    localStorage.setItem(cacheKey, JSON.stringify(personas));
  } catch {
    // Storage may be full or disabled; the list still works for this session.
  }
  return personas;
};

/** A persona's display name, whichever shape the CRM returned it in. */
export const personaName = (persona: FabaiPersona): string =>
  String(persona.name || persona.label || persona._id || persona.id || '');

/** A persona's id, whichever shape the CRM returned it in. */
export const personaId = (persona: FabaiPersona): string =>
  String(persona.id || persona._id || '');

/**
 * Normalizes a stored persona value to the CRM id: ids pass through, a
 * legacy value that matches a persona's name maps to that persona's id, and
 * anything unknown (a deleted persona, the editor's free-text fallback) is
 * left as stored.
 */
export const resolvePersonaId = (
  value: string | null | undefined,
  personas?: FabaiPersona[] | null,
): string => {
  const raw = String(value || '').trim();
  if (!raw || !personas || personas.length === 0) return raw;
  if (personas.some((persona) => personaId(persona) === raw)) return raw;
  const byName = personas.find((persona) => personaName(persona) === raw);
  return (byName && personaId(byName)) || raw;
};

/** The display name for a stored persona value (an id or a legacy name). */
export const personaDisplayName = (
  value: string | null | undefined,
  personas?: FabaiPersona[] | null,
): string => {
  const raw = String(value || '');
  if (!raw || !personas || personas.length === 0) return raw;
  const persona =
    personas.find((candidate) => personaId(candidate) === raw) ||
    personas.find((candidate) => personaName(candidate) === raw);
  return persona ? personaName(persona) : raw;
};
